import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserCog } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { staff } from "@/api/endpoints";
import type { StaffRole } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  ErrorState,
  FormError,
  Spinner,
  TableSkeleton,
} from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDateTime, humanize } from "@/lib/utils";

const ROLES: { value: StaffRole; label: string; blurb: string }[] = [
  {
    value: "SUPPORT_L1",
    label: "Support L1",
    blurb: "Read users and bookings, handle tickets",
  },
  {
    value: "SUPPORT_L2",
    label: "Support L2",
    blurb: "Everything L1 plus refunds and account status",
  },
  {
    value: "KYC_REVIEWER",
    label: "KYC reviewer",
    blurb: "View identity data and approve providers",
  },
  {
    value: "CATALOG_MANAGER",
    label: "Catalog manager",
    blurb: "Categories, banners and the app home screen",
  },
  {
    value: "FINANCE",
    label: "Finance",
    blurb: "Wallet adjustments, payments, audit trail",
  },
  {
    value: "SUPER_ADMIN",
    label: "Super admin",
    blurb: "Full access including configuration and staff",
  },
];

export default function Staff() {
  const [creating, setCreating] = React.useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ["staff"], queryFn: staff.list });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      staff.update(id, { status }),
    onSuccess: () => {
      toast.success("Staff account updated");
      void queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (err) =>
      toast.error(
        "Could not update the account",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  return (
    <>
      <PageHeader
        title="Staff accounts"
        description="Who can sign in to this console, and what each of them can do."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Add staff
          </Button>
        }
      />

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={5} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>2FA</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last sign-in</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.full_name}</TableCell>
                  <TableCell className="text-muted-foreground">{s.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {s.roles.map((r) => (
                        <Badge key={r} variant="outline">
                          {humanize(r)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={s.totp_enabled ? "good" : "warning"}>
                      {s.totp_enabled ? "Enabled" : "Not set up"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={toneFor(s.status)}>{humanize(s.status)}</Badge>
                  </TableCell>
                  <TableCell className="tabular text-muted-foreground">
                    {formatDateTime(s.last_login_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate({
                          id: s.id,
                          status: s.status === "ACTIVE" ? "DISABLED" : "ACTIVE",
                        })
                      }
                    >
                      {s.status === "ACTIVE" ? "Disable" : "Enable"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            icon={UserCog}
            title="No staff accounts"
            description="Create the first one from the command line, then manage the rest here."
          />
        )}
      </Card>

      {creating && <CreateStaffDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateStaffDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [roles, setRoles] = React.useState<StaffRole[]>(["SUPPORT_L1"]);
  const [error, setError] = React.useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      staff.create({
        email: email.trim(),
        full_name: fullName.trim(),
        password,
        roles,
      }),
    onSuccess: () => {
      toast.success("Staff account created", "Ask them to set up 2FA on first login.");
      void queryClient.invalidateQueries({ queryKey: ["staff"] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not create the account."),
  });

  function toggleRole(role: StaffRole) {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a staff account</DialogTitle>
          <DialogDescription>
            They sign in with email and password, then set up an authenticator app.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (roles.length === 0) {
              setError("Pick at least one role.");
              return;
            }
            create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="full-name">Full name</Label>
            <Input
              id="full-name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="staff-email">Work email</Label>
            <Input
              id="staff-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@kheti24.in"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="staff-password">Temporary password</Label>
            <Input
              id="staff-password"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <p className="text-[11px] text-muted-foreground">
              At least 12 characters. They can change it after signing in.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Roles</Label>
            <div className="space-y-1">
              {ROLES.map((r) => (
                <label
                  key={r.value}
                  className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-xs hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    checked={roles.includes(r.value)}
                    onChange={() => toggleRole(r.value)}
                    className="mt-0.5 size-3.5 accent-[hsl(var(--primary))]"
                  />
                  <span>
                    <span className="font-medium">{r.label}</span>
                    <span className="block text-muted-foreground">{r.blurb}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <FormError message={error} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Spinner />}
              Create account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
